import { useEffect, useMemo, useState } from "react";
import WebApp from "@twa-dev/sdk";
import {
  CounterLpVault,
  calcPositionPnl,
  enrichAxmPools,
  fetchIndexerPools,
  filterMarketsByMinTvlUsd,
  pinMarketsFirst,
  formatCompact,
  fromMicro,
  getNativeBalance,
  queryPoolInfo,
  shortAddress,
  type EnrichedMarket,
  type PositionPnl,
} from "@axiome/counter-lp-sdk";
import { useAxiomeWallet } from "./hooks/useAxiomeWallet";
import { ConnectModal } from "./components/ConnectModal";
import { PoolCard } from "./components/PoolCard";
import { PoolDetail } from "./components/PoolDetail";
import { PnlCard } from "./components/PnlCard";
import { SupplyTokenPanel } from "./components/SupplyTokenPanel";
import { INDEXER_URL, REST_URL, VAULT_ADDRESS, HAS_LIVE_VAULT } from "./lib/config";
import { friendlyError } from "./lib/friendlyError";

type Tab = "earn" | "positions" | "project" | "about";

type StoredPos = {
  symbol: string;
  axm_deposited: string;
  token_matched: string;
  lp_amount: string;
  pending_pool_rewards: string;
  pending_community_rewards: string;
  pending_token_rewards?: string;
};

export default function App() {
  const wallet = useAxiomeWallet();
  const [tab, setTab] = useState<Tab>("earn");
  const [markets, setMarkets] = useState<EnrichedMarket[]>([]);
  const [loadingPools, setLoadingPools] = useState(true);
  const [activePool, setActivePool] = useState<string | null>(null);
  const [balance, setBalance] = useState<string | null>(null);
  const [positions, setPositions] = useState<StoredPos[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [liveVault, setLiveVault] = useState(false);
  const [query, setQuery] = useState("");
  const [vaultMarkets, setVaultMarkets] = useState<
    Array<{ symbol: string; pool: string; token: string; total_lp: string }>
  >([]);
  const [depositorPoolShare, setDepositorPoolShare] = useState(0.9);
  const [depositorCommunityShare, setDepositorCommunityShare] = useState(0.8);
  const [supplierOfPlatform, setSupplierOfPlatform] = useState(0.4);
  const [minSupplyAxm, setMinSupplyAxm] = useState(500_000);
  const [posTick, setPosTick] = useState(0);
  const [withdrawEst, setWithdrawEst] = useState<Record<string, string>>({});
  const [poolLpSupply, setPoolLpSupply] = useState<Record<string, string>>({});

  const vault = useMemo(
    () =>
      new CounterLpVault({
        vaultAddress: VAULT_ADDRESS,
        restUrl: REST_URL,
      }),
    [],
  );

  const selectedMarket = activePool
    ? markets.find((m) => m.id === activePool) ?? null
    : null;

  const filteredMarkets = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return markets;
    return markets.filter(
      (m) =>
        m.symbol.toLowerCase().includes(q) ||
        m.tokenName.toLowerCase().includes(q) ||
        m.token.toLowerCase().includes(q),
    );
  }, [markets, query]);

  function openPool(id: string) {
    setActivePool(id);
    setTab("earn");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function closePool() {
    setActivePool(null);
  }

  function switchTab(next: Tab) {
    setActivePool(null);
    setTab(next);
  }

  useEffect(() => {
    try {
      WebApp.ready();
      WebApp.expand();
      WebApp.setHeaderColor("#000000");
      WebApp.setBackgroundColor("#000000");
    } catch {
      /* browser */
    }
  }, []);

  useEffect(() => {
    let alive = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    async function loadMarkets() {
      setLoadingPools(true);
      try {
        const [raw, onchain] = await Promise.all([
          fetchIndexerPools({ perPage: 100, indexer: INDEXER_URL }).catch(() => []),
          HAS_LIVE_VAULT
            ? vault.markets()
            : Promise.resolve([] as Awaited<ReturnType<typeof vault.markets>>),
        ]);
        if (!alive) return;
        const enriched = enrichAxmPools(raw);
        const listed = pinMarketsFirst(filterMarketsByMinTvlUsd(enriched, 100));
        if (!onchain.length) {
          setMarkets([]);
          setVaultMarkets([]);
          return;
        }
        setVaultMarkets(
          onchain.map((m) => ({
            symbol: m.symbol,
            pool: m.pool,
            token: m.token,
            total_lp: m.total_lp,
          })),
        );
        const byPool = new Map(onchain.map((m) => [m.pool.toLowerCase(), m]));
        const reconciled = listed
          .filter((m) => byPool.has(m.pool.toLowerCase()))
          .map((m) => {
            const hit = byPool.get(m.pool.toLowerCase())!;
            return {
              ...m,
              symbol: hit.symbol,
              pool: hit.pool,
              token: hit.token,
              vaultTotalLp: hit.total_lp,
              inventory: hit.inventory,
              active: hit.active,
            };
          });
        for (const vm of onchain) {
          if (!reconciled.some((r) => r.pool.toLowerCase() === vm.pool.toLowerCase())) {
            reconciled.push({
              id: vm.pool,
              symbol: vm.symbol,
              tokenName: vm.symbol,
              pool: vm.pool,
              token: vm.token,
              tokenDecimals: vm.token_decimals ?? 6,
              apr: 0,
              tvlAxm: 0,
              tvlUsd: 0,
              vol24Axm: 0,
              fees24Axm: 0,
              effectiveApr: 0,
              priceTokenInAxm: 0,
              reserveAxm: "0",
              reserveToken: "0",
              poolFeePercent: 0.3,
              vaultTotalLp: vm.total_lp,
              inventory: vm.inventory,
              active: vm.active,
            });
          }
        }
        setMarkets(reconciled);
      } catch {
        if (!alive) return;
        setMarkets([]);
        retryTimer = setTimeout(() => {
          if (alive) void loadMarkets();
        }, 8_000);
      } finally {
        if (alive) setLoadingPools(false);
      }
    }

    void loadMarkets();
    return () => {
      alive = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [vault]);

  useEffect(() => {
    let alive = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    async function refreshVault() {
      if (!HAS_LIVE_VAULT) {
        setLiveVault(false);
        return;
      }
      try {
        const [c, m] = await Promise.all([vault.config(), vault.markets()]);
        if (!alive) return;
        setLiveVault(true);
        const share = Number(c.depositor_pool_share);
        if (Number.isFinite(share) && share > 0 && share <= 1) {
          setDepositorPoolShare(share);
        }
        const cShare = Number(c.depositor_community_share);
        if (Number.isFinite(cShare) && cShare > 0 && cShare <= 1) {
          setDepositorCommunityShare(cShare);
        }
        const sShare = Number(c.supplier_of_platform_share);
        if (Number.isFinite(sShare) && sShare > 0 && sShare <= 1) {
          setSupplierOfPlatform(sShare);
        }
        if (c.min_supply_axm) {
          const minH = Number(c.min_supply_axm) / 1e6;
          if (Number.isFinite(minH) && minH > 0) setMinSupplyAxm(minH);
        }
        if (m?.length) {
          setVaultMarkets(
            m.map((x) => ({
              symbol: x.symbol,
              pool: x.pool,
              token: x.token,
              total_lp: x.total_lp,
            })),
          );
        }
      } catch {
        if (!alive) return;
        setLiveVault(false);
        retryTimer = setTimeout(() => {
          if (alive) void refreshVault();
        }, 8_000);
      }
    }

    void refreshVault();
    return () => {
      alive = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [vault]);

  useEffect(() => {
    if (!wallet.account) {
      setBalance(null);
      setPositions([]);
      return;
    }
    getNativeBalance(wallet.account, "uaxm", REST_URL)
      .then(setBalance)
      .catch(() => setBalance(null));

    if (!liveVault) {
      setPositions([]);
      return;
    }

    vault
      .positions(wallet.account)
      .then((p) => setPositions((p as StoredPos[]) ?? []))
      .catch(() => setPositions([]));
  }, [wallet.account, vault, liveVault, posTick]);

  useEffect(() => {
    if (!wallet.account || !liveVault || positions.length === 0) {
      setWithdrawEst({});
      setPoolLpSupply({});
      return;
    }
    let alive = true;
    (async () => {
      const est: Record<string, string> = {};
      const supplies: Record<string, string> = {};
      await Promise.all(
        positions.map(async (pos) => {
          try {
            const prev = await vault.previewWithdraw(wallet.account!, pos.symbol);
            if (prev?.estimated_axm) est[pos.symbol] = prev.estimated_axm;
          } catch {
            /* soft */
          }
          const pool =
            markets.find((m) => m.symbol === pos.symbol)?.pool ||
            vaultMarkets.find((m) => m.symbol === pos.symbol)?.pool;
          if (!pool) return;
          try {
            const info = await queryPoolInfo(pool, REST_URL);
            if (info?.lp_token_supply) supplies[pos.symbol] = info.lp_token_supply;
            if (info?.token1_reserve && info?.token2_reserve) {
              // keep markets reserve fresh when possible — handled via supplies only
            }
          } catch {
            /* soft */
          }
        }),
      );
      if (!alive) return;
      setWithdrawEst(est);
      setPoolLpSupply(supplies);
    })();
    return () => {
      alive = false;
    };
  }, [wallet.account, liveVault, positions, vault, markets, vaultMarkets]);

  const pnlList: PositionPnl[] = useMemo(() => {
    return positions.map((pos) => {
      const m = markets.find((x) => x.symbol === pos.symbol);
      return calcPositionPnl({
        symbol: pos.symbol,
        axmDepositedMicro: pos.axm_deposited,
        tokenMatchedMicro: pos.token_matched,
        lpAmountMicro: pos.lp_amount,
        pendingPoolMicro: pos.pending_pool_rewards,
        pendingCommunityMicro: pos.pending_community_rewards,
        pendingTokenMicro: pos.pending_token_rewards,
        tokenDecimals: m?.tokenDecimals ?? 6,
        priceTokenInAxm: m?.priceTokenInAxm ?? 0,
        reserveAxmMicro: m?.reserveAxm,
        reserveTokenMicro: m?.reserveToken,
        poolLpSupplyMicro: poolLpSupply[pos.symbol] || m?.lpTokenSupply,
        estimatedWithdrawAxmMicro: withdrawEst[pos.symbol],
        depositorPoolShare,
      });
    });
  }, [positions, markets, depositorPoolShare, poolLpSupply, withdrawEst]);

  async function refreshPositionsSoon() {
    // Connect may return before indexer/LCD settles — refresh twice
    setPosTick((t) => t + 1);
    window.setTimeout(() => setPosTick((t) => t + 1), 2500);
    window.setTimeout(() => setPosTick((t) => t + 1), 6000);
  }

  const totals = useMemo(() => {
    const avgApr =
      markets.slice(0, 10).reduce((s, m) => s + m.effectiveApr, 0) /
      Math.max(1, Math.min(10, markets.length));
    const tvl = markets.reduce((s, m) => s + m.tvlAxm, 0);
    const pnl = pnlList.reduce((s, p) => s + p.pnlValueAxm, 0);
    return { avgApr, tvl, pnl };
  }, [markets, pnlList]);

  async function provide(axm: string) {
    if (!wallet.account) throw new Error("Подключите кошелёк Axiome Connect");
    if (!selectedMarket) throw new Error("Выберите пару");
    if (!liveVault) throw new Error("Сеть обновляется — попробуйте через минуту");

    try {
      let liveMsgs = await vault.buildProvideMsgWithSlippage({
        sender: wallet.account,
        symbol: selectedMarket.symbol,
        axmHuman: axm,
      });
      try {
        await wallet.sign(liveMsgs);
      } catch (e) {
        const raw = e instanceof Error ? e.message : String(e);
        // Retry once with Harvest if chain requires it (fees accrued)
        if (/HarvestRequired|harvest required/i.test(raw)) {
          liveMsgs = await vault.buildProvideMsgWithSlippage({
            sender: wallet.account,
            symbol: selectedMarket.symbol,
            axmHuman: axm,
            withHarvest: true,
          });
          await wallet.sign(liveMsgs);
        } else {
          throw e;
        }
      }
      setStatus("Внесение отправлено — позиция появится во вкладке «Позиции»");
      setActivePool(null);
      setTab("positions");
      await refreshPositionsSoon();
    } catch (e) {
      throw new Error(friendlyError(e));
    }
  }

  async function withdraw(symbol: string) {
    if (!wallet.account) return;
    if (!liveVault) {
      setStatus("Сеть обновляется — попробуйте через минуту");
      return;
    }
    try {
      const msgs = await vault.buildWithdrawMsgWithSlippage({
        sender: wallet.account,
        symbol,
        shareBps: 10_000,
      });
      await wallet.sign(msgs);
      setStatus("Вывод отправлен — обновите позиции через несколько секунд");
      await refreshPositionsSoon();
    } catch (e) {
      setStatus(friendlyError(e));
    }
  }

  async function claim(symbol?: string) {
    if (!wallet.account) return;
    if (!liveVault) {
      setStatus("Сеть обновляется — попробуйте через минуту");
      return;
    }
    try {
      const symbols = symbol ? [symbol] : positions.map((p) => p.symbol);
      const msgs = await vault.buildClaimMsgs({
        sender: wallet.account,
        symbol,
        symbols,
      });
      await wallet.sign(msgs);
      setStatus("Запрос на доход отправлен — подтвердите в Axiome Wallet");
      await refreshPositionsSoon();
    } catch (e) {
      setStatus(friendlyError(e));
    }
  }

  return (
    <div className="app-shell">
      <div className="wallet-bar">
        <div className="wallet-chip">
          {wallet.account
            ? `${shortAddress(wallet.account)}${balance ? ` · ${fromMicro(balance)} AXM` : ""}`
            : "Подключите кошелёк"}
        </div>
        {wallet.account ? (
          <button className="btn btn-ghost btn-sm" onClick={wallet.disconnect}>
            Выйти
          </button>
        ) : (
          <button className="btn btn-primary btn-sm" onClick={wallet.startConnect}>
            Войти
          </button>
        )}
      </div>

      {tab === "earn" && selectedMarket ? (
        <PoolDetail
          market={selectedMarket}
          vault={vault}
          account={wallet.account}
          requirePreview={liveVault}
          onBack={closePool}
          onProvide={provide}
          onConnect={wallet.startConnect}
        />
      ) : null}

      {tab === "earn" && !selectedMarket ? (
        <>
          <h1 className="brand">
            <span>Axiome Counter LP</span>
            <em>Ликвидность одним AXM</em>
          </h1>
          <p className="hero-copy">
            Вносите только AXM — второй токен добавляет платформа. Доход пула и
            community-начисления можно забрать в любой момент; при закрытии позиции
            вы получаете AXM.
          </p>

          <div className="stat-strip">
            <div>
              <span>Средняя доходность</span>
              <b>{totals.avgApr ? `${totals.avgApr.toFixed(1)}%` : "—"}</b>
            </div>
            <div>
              <span>Ликвидность</span>
              <b>{formatCompact(totals.tvl)} </b>
            </div>
            <div>
              <span>Ваш результат</span>
              <b style={{ color: totals.pnl >= 0 ? "var(--ok)" : "var(--danger)" }}>
                {formatCompact(totals.pnl)}
              </b>
            </div>
          </div>

          {!wallet.account ? (
            <div className="cta-row">
              <button className="btn btn-primary" onClick={wallet.startConnect}>
                Подключить кошелёк
              </button>
            </div>
          ) : null}

          <div className="section">
            <h2>Рынки</h2>
            <p className="lead">Выберите пару и внесите AXM</p>
            <div className="field" style={{ marginTop: 0 }}>
              <label>Поиск токена</label>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Например: CHAPA, AXP, SIMBA"
              />
            </div>
            {loadingPools ? (
              <>
                <div className="skeleton" />
                <div className="skeleton" />
                <div className="skeleton" />
              </>
            ) : filteredMarkets.length === 0 ? (
              <div className="empty">
                {markets.length === 0
                  ? liveVault
                    ? "Скоро появятся первые пары"
                    : "Подключаемся к сети…"
                  : `По запросу «${query}» ничего не найдено`}
              </div>
            ) : (
              filteredMarkets.map((m) => (
                <PoolCard
                  key={m.id}
                  market={m}
                  selected={false}
                  onSelect={() => openPool(m.id)}
                />
              ))
            )}
          </div>
        </>
      ) : null}

      {tab === "positions" ? (
        <div className="section">
          <h2>Мои позиции</h2>
          <p className="lead">Внесённый AXM и текущая оценка результата</p>
          {pnlList.length === 0 ? (
            <div className="empty">Пока нет позиций — откройте пару во вкладке Earn</div>
          ) : (
            pnlList.map((p) => (
              <PnlCard
                key={p.symbol}
                pnl={p}
                onClaim={() => claim(p.symbol)}
                onWithdraw={() => withdraw(p.symbol)}
              />
            ))
          )}
        </div>
      ) : null}

      {tab === "project" ? (
        <SupplyTokenPanel
          markets={markets}
          account={wallet.account}
          liveEnabled={liveVault}
          minSupplyAxm={minSupplyAxm}
          supplierOfPlatform={supplierOfPlatform}
          userPoolShare={depositorPoolShare}
          userLpBonusShare={depositorCommunityShare}
          onConnect={wallet.startConnect}
          onSign={async (msgs) => {
            if (!liveVault) {
              throw new Error("Сеть обновляется — попробуйте через минуту");
            }
            await wallet.sign(msgs);
          }}
        />
      ) : null}

      {tab === "about" ? (
        <div className="section">
          <h2>Как это работает</h2>
          <p className="lead">Ликвидность в пары к AXM — без второго токена на руках.</p>
          <ol style={{ color: "var(--muted)", lineHeight: 1.55, paddingLeft: 18 }}>
            <li>Подключите кошелёк через Axiome Connect</li>
            <li>Выберите пару и внесите только AXM</li>
            <li>Второй токен добавляет платформа или владелец проекта</li>
            <li>
              Доход можно забрать отдельно; при выводе позиции вы получаете AXM. LP остаётся
              на контракте сервиса, а не на вашем адресе.
            </li>
          </ol>
        </div>
      ) : null}

      {status ? <p className="status">{status}</p> : null}
      {wallet.error ? <p className="status err">{wallet.error}</p> : null}

      <nav className="nav">
        <button
          className={tab === "earn" && !selectedMarket ? "active" : ""}
          onClick={() => switchTab("earn")}
        >
          Earn
        </button>
        <button className={tab === "positions" ? "active" : ""} onClick={() => switchTab("positions")}>
          Позиции
        </button>
        <button className={tab === "project" ? "active" : ""} onClick={() => switchTab("project")}>
          Проект
        </button>
        <button className={tab === "about" ? "active" : ""} onClick={() => switchTab("about")}>
          О сервисе
        </button>
      </nav>

      {wallet.connecting && wallet.authCode && wallet.qrPayload ? (
        <ConnectModal
          code={wallet.authCode}
          qrPayload={wallet.qrPayload}
          onClose={wallet.cancelConnect}
        />
      ) : null}
    </div>
  );
}
